import { useEffect, useRef } from 'react';
import { mobilizeiDevicesService } from '../services/mobilizei/devices.service';
import mixpanel from 'mixpanel-browser';
import { mixpanelEvents } from '../utils/constants';

const POLL_INTERVAL_MS = 4000;
const TIMEOUT_MS = 60000;
const POLL_STOP_MS = 300000

/**
 * Polls the backend until a device registers itself as having opened this store
 * in the Mobilizei Visualizador.
 *
 * Flipping `enabled` off and on again starts a brand new run: the clocks behind
 * `onTimeout` and `onStopped` reset, which is how the screen resumes a paused
 * search.
 */
export default function usePreviewerDetection({ platformId, storeId, enabled = true, onDetected, onTimeout, onStopped }) {
    const onDetectedRef = useRef(onDetected);
    const onTimeoutRef = useRef(onTimeout);
    const onStoppedRef = useRef(onStopped);
    const alreadyTimeout = useRef(false)

    useEffect(() => { onDetectedRef.current = onDetected; }, [onDetected]);
    useEffect(() => { onTimeoutRef.current = onTimeout; }, [onTimeout]);
    useEffect(() => { onStoppedRef.current = onStopped; }, [onStopped]);

    useEffect(() => {
        if (!enabled || !platformId || !storeId) {
            return;
        }

        let timeoutId;
        let cancelled = false;
        const startedAt = Date.now();

        alreadyTimeout.current = false;

        const poll = async () => {
            try {
                const response = await mobilizeiDevicesService.getPreviewerDeviceStatus(platformId, storeId);

                if (cancelled) return;

                if (response?.connected) {
                    mixpanel.track(mixpanelEvents.ONBOARDING_PREVIEWER_DETECTED, {
                        'platform_id': platformId,
                        'store_id': storeId,
                        'mobile_platform_id': response.device.osName,
                        'time_to_detection_in_seconds': Math.round((Date.now() - startedAt) / 1000)
                    })
                    onDetectedRef.current?.(response.device);
                    return;
                }
            } catch (error) {
                // A failing tick is expected while the merchant is still installing
                // the app, and a transient 500 must not cost them the screen. Swallow
                // it and try again on the next tick.
            }

            if (cancelled) return;

            if (Date.now() - startedAt >= TIMEOUT_MS && !alreadyTimeout.current) {
                onTimeoutRef.current?.();
                alreadyTimeout.current = true
            }

            // Nobody watches this screen for ten minutes straight. Stop hitting the
            // backend and let the screen say so — it offers a way to start over.
            if (Date.now() - startedAt >= POLL_STOP_MS) {
                onStoppedRef.current?.();
                return;
            }

            // Chained timeout instead of setInterval: a slow response must not let
            // requests pile up on top of each other.
            timeoutId = setTimeout(poll, POLL_INTERVAL_MS);
        };

        poll();

        return () => {
            cancelled = true;
            clearTimeout(timeoutId);
        };
    }, [platformId, storeId, enabled]);
}
